import React, {useState, useEffect, useId} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import NavBar from '../pages/NavBar/NavBar';
import Button from '../common/Button';
import ReactDataGrid from '@inovua/reactdatagrid-community';
import '@inovua/reactdatagrid-community/index.css';
import {toast} from 'react-toastify';
import {getIdMeBaseApiUrl} from "../../utils/UrlConstants";
import FooterData from "../pages/NavBar/FooterData";
import {checkUserAuthentication, handleUnauthenticatedUser} from "../../utils/authUtils";
import {base64Utils} from "../../utils/base64Utils";
import {useApplications} from "../../hooks/useApplications";
import {displayPhoneNumber} from "../../utils/phoneUtils";
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import InputText from '../common/InputText';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useIdentityAppService } from '../../services/IdentityServices';

interface UserDto {
    userId: number;
    userLogin: string;
    userType: string;
    userEmail: string;
    userPhone: string;
    userIdpGuid: string;
    firstName: string;
    lastName: string;
    createdDate: string;
    modifiedDate: string;
    notes: string;
    userStatus: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zip?: string;
    organizationName?: string;
    organizationType?: string;
    stateIssuedId?: string;
}

interface UserAppRequestDto {
    id: number;
    userId: number;
    userLogin: string;
    firstName: string;
    lastName: string;
    userEmail: string;
    applicationCode: string;
    applicationName: string;
    roleCode: string;
    roleName: string;
    assignedTo: string;
    status: string;
    operationalFlag: string;
    createdDate: string;
    modifiedDate: string;
    createdBy: string;
    modifiedBy: string;
    notes: string;
}

interface UserGroupsResponse {
    success: boolean;
    groups: UserGroup[];
    message: string;
    timestamp: string;
}

interface UserGroup {
    groupId: string;
    groupName: string;
    description: string;
    groupType: string;
    applicationCode: string;
    applicationName: string;
    assignedDate: string;
    requestCreatedDate?: string;
    startDate?: string;
    endDate?: string
}

interface UserHistoryDto {
    historyId: number;
    userId: number;
    userLogin: string;
    userType: string;
    userEmail: string;
    userPhone: string;
    userIdpGuid: string;
    firstName: string;
    lastName: string;
    operationalFlag: string;
    notes: string;
    userStatus: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
    actionType: string;
    changeDescription: string;
    createdDate: string;
    modifiedDate: string;
    createdBy: string;
    modifiedBy: string;
}

const UserManagement: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const {dynamicApplications} = useApplications();

    const user: UserDto = location.state?.user || null;
    const userTypeSelection: 'Internal' | 'External' = location.state?.userTypeSelection || 'External';
    const searchState = location.state?.searchState;

    // State management
    const [loading, setLoading] = useState(false);
    const [resendingEmail, setResendingEmail] = useState(false);
    const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
    const [userRequests, setUserRequests] = useState<UserAppRequestDto[]>([]);
    const [filteredGroups, setFilteredGroups] = useState<UserGroup[]>([]);
    const [filteredRequests, setFilteredRequests] = useState<UserAppRequestDto[]>([]);
    const [groupsLoading, setGroupsLoading] = useState(true);
    const [requestsLoading, setRequestsLoading] = useState(true);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
    const [userHistory, setUserHistory] = useState<UserHistoryDto[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    // Request details dialog state
    const [requestDetailsDialogOpen, setRequestDetailsDialogOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState<UserAppRequestDto | null>(null);
    const [requestLifecycle, setRequestLifecycle] = useState<UserAppRequestDto[]>([]);
    const [requestLifecycleLoading, setRequestLifecycleLoading] = useState(false);

    // Revoke dialog state
    const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState<UserGroup | null>(null);
    const [revokeOption, setRevokeOption] = useState<'immediate' | 'date'>('immediate');
    const [revokeDate, setRevokeDate] = useState<string>(() => {
        const today = new Date();
        return today.toISOString().split('T')[0]; // Format: YYYY-MM-DD
    });
    const [revokeNotes, setRevokeNotes] = useState<string>('');
    const [revokingGroup, setRevokingGroup] = useState(false);

    // Load user's assigned groups and requests
    useEffect(() => {
        if (user) {
            loadUserGroups();
            loadUserRequests();
        }
    }, [user]);

    // Filter groups and requests based on admin's applications
    useEffect(() => {
        if (dynamicApplications && dynamicApplications.length > 0) {
            // Get application names that the admin has access to
            const adminAppNames = dynamicApplications.map(app => app.applicationName.toLowerCase());

            // Filter groups by applications
            const filtered = userGroups.filter(group => {
                // Check if the group's application matches any of the admin's applications
                return adminAppNames.includes(group.applicationName?.toLowerCase());
            });
            setFilteredGroups(filtered);

            // Filter requests by applications
            const filteredReqs = userRequests.filter(request => {
                // Check if the request's application matches any of the admin's applications
                return adminAppNames.includes(request.applicationName?.toLowerCase());
            });
            setFilteredRequests(filteredReqs);
        } else {
            // If no applications or not loaded yet, show all
            setFilteredGroups(userGroups);
            setFilteredRequests(userRequests);
        }
    }, [dynamicApplications, userGroups, userRequests]);

    // Redirect if no user data
    React.useEffect(() => {
        if (!checkUserAuthentication()) {
            handleUnauthenticatedUser();
            return;
        }

        if (!user) {
            setErrors(prev => ({
                ...prev,
                general: 'No user data found. Redirecting to User Management.'
            }));
            navigate('/external-user-management');
        }
    }, [user, navigate]);

    const loadUserGroups = async () => {
        try {
            setGroupsLoading(true);
            const url = `${getIdMeBaseApiUrl()}/user/${user.userId}/groups`;

            const response = await useIdentityAppService().getUserGroups(user.userId);

            if (response.ok) {
                const data: UserGroupsResponse = await response.json();
                setUserGroups(data.groups || []);
            } else {
                console.error('Failed to load user groups');
                setUserGroups([]);
            }
        } catch (error) {
            console.error('Error loading user groups:', error);
            setUserGroups([]);
        } finally {
            setGroupsLoading(false);
        }
    };

    const loadUserRequests = async () => {
        try {
            setRequestsLoading(true);

            const response = await useIdentityAppService().fetchUserRequests(user.userId);

            if (response.ok) {
                const data = await response.json();
                setUserRequests(data.requests || []);
            } else {
                console.error('Failed to load user requests');
                setUserRequests([]);
            }
        } catch (error) {
            console.error('Error loading user requests:', error);
            setUserRequests([]);
        } finally {
            setRequestsLoading(false);
        }
    };

    const handleResendVerificationEmail = async () => {
        try {
            setResendingEmail(true);

            const url = `${getIdMeBaseApiUrl()}/user/${user.userId}/resend-verification`;

            const response = await useIdentityAppService().postResendVerificationEmail(user.userId);

            if (response.ok) {
                toast.success('Verification email sent successfully!');
                // Clear any previous errors on success
                setErrors(prev => ({
                    ...prev,
                    resendEmail: ''
                }));
            } else {
                const errorData = await response.json();
                setErrors(prev => ({
                    ...prev,
                    resendEmail: errorData.message || 'Failed to send verification email'
                }));
            }
        } catch (error) {
            console.error('Error sending verification email:', error);
            setErrors(prev => ({
                ...prev,
                resendEmail: 'Network error occurred while sending verification email'
            }));
        } finally {
            setResendingEmail(false);
        }
    };

    const handleBack = () => {
        navigate('/admin-dashboard', {
            state: {
                userTypeSelection: userTypeSelection,
                searchState
            }
        });
    };

    const handleViewRequestDetails = async (request: UserAppRequestDto) => {
        setSelectedRequest(request);
        setRequestDetailsDialogOpen(true);
        setRequestLifecycleLoading(true);

        try {
            const response = await useIdentityAppService().fetchRequestLifecycle(request.id);

            if (response.ok) {
                const lifecycleData: UserAppRequestDto[] = await response.json();
                setRequestLifecycle(lifecycleData);
            } else {
                console.error('Failed to fetch request lifecycle:', response.statusText);
                setRequestLifecycle([]);
            }
        } catch (error) {
            console.error('Error fetching request lifecycle:', error);
            setRequestLifecycle([]);
        } finally {
            setRequestLifecycleLoading(false);
        }
    };

    const handleCloseRequestDetailsDialog = () => {
        setRequestDetailsDialogOpen(false);
        setSelectedRequest(null);
        setRequestLifecycle([]);
    };

    const handleEditUser = () => {
        // Navigate to edit account page with user data
        navigate('/admin/edit-account', {
            state: {
                user: user,
                returnPath: '/admin/user-management'
            }
        });
    };

    const handleViewHistory = async () => {
        setHistoryLoading(true);
        setHistoryDialogOpen(true);

        try {
            const response = await useIdentityAppService().getUserHistory(user);

            if (response.status === 200 && response.data.success) {
                setUserHistory(response.data.history || []);
            } else {
                toast.error('Failed to load user history');
                setUserHistory([]);
            }
        } catch (error) {
            console.error('Error loading user history:', error);
            toast.error('Error loading user history');
            setUserHistory([]);
        } finally {
            setHistoryLoading(false);
        }
    };

    const handleCloseHistoryDialog = () => {
        setHistoryDialogOpen(false);
    };

    // Handle opening the revoke dialog
    const handleRevokeClick = (group: UserGroup) => {
        setSelectedGroup(group);
        setRevokeDialogOpen(true);
        setRevokeOption('immediate');
        // Set revoke date to today
        const today = new Date();
        setRevokeDate(today.toISOString().split('T')[0]);
        setRevokeNotes('');
    };

    // Handle closing the revoke dialog
    const handleCloseRevokeDialog = () => {
        setRevokeDialogOpen(false);
        setSelectedGroup(null);
        setRevokeOption('immediate');
        // Reset revoke date to today
        const today = new Date();
        setRevokeDate(today.toISOString().split('T')[0]);
        setRevokeNotes('');
    };

    // Handle revoke option change
    const handleRevokeOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value as 'immediate' | 'date';
        setRevokeOption(value);
    };

    // Handle ReactQuill notes change
    const handleRevokeNotesChange = (content: string) => {
        setRevokeNotes(content);
    };

    // Check if submit button should be enabled
    const isRevokeSubmitEnabled = () => {
        // Notes are always mandatory
        // ReactQuill may return '<p><br></p>' for empty content
        const strippedNotes = revokeNotes.replace(/<[^>]*>/g, '').trim();
        if (!strippedNotes) {
            return false;
        }
        // If date option is selected, date must be provided
        if (revokeOption === 'date' && !revokeDate) {
            return false;
        }
        return true;
    };

    // Handle revoke date change
    const handleRevokeDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRevokeDate(event.target.value);
    };

    // Handle revoke submit
    const handleRevokeSubmit = async () => {
        if (!selectedGroup || !isRevokeSubmitEnabled()) {
            return;
        }

        try {
            setRevokingGroup(true);

            const url = `${getIdMeBaseApiUrl()}/user/group/revoke`;

            const requestBody = {
                userId: user.userId,
                groupId: selectedGroup.groupId,
                applicationCode: selectedGroup.applicationCode,
                revokeType: revokeOption,
                revokeDate: revokeOption === 'date' ? revokeDate : null,
                notes: base64Utils.encode(revokeNotes)
            };

            const response = await useIdentityAppService().postRevokeUserGroup(JSON.stringify(requestBody));

            if (response.ok) {
                toast.success(`Group access ${revokeOption === 'immediate' ? 'revoked immediately' : 'scheduled for revocation'} successfully!`);
                handleCloseRevokeDialog();
                // Reload user groups and requests to reflect the change
                loadUserGroups();
                loadUserRequests();
            } else {
                const errorData = await response.json();
                toast.error(errorData.message || 'Failed to revoke group access');
            }
        } catch (error) {
            console.error('Error revoking group access:', error);
            toast.error('Network error occurred while revoking group access');
        } finally {
            setRevokingGroup(false);
        }
    };

    // Function to get status styles
    const getStatusStyles = (status: string) => {
        switch (status?.toUpperCase()) {
            case 'ACTIVE':
                return {
                    backgroundColor: '#E8F5E8',
                    color: '#4CAF50'
                };
            case 'APPROVED':
                return {
                    backgroundColor: '#E8F5E8',
                    color: '#4CAF50'
                };
            case 'PENDING':
            case 'REQUESTED':
                return {
                    backgroundColor: '#fff3cd',
                    color: '#856404'
                };
            case 'SUSPENDED':
            case 'DISABLED':
                return {
                    backgroundColor: '#FFF3E0',
                    color: '#FF9800'
                };
            case 'LOCKED':
                return {
                    backgroundColor: '#FFEBEE',
                    color: '#f44336'
                };
            case 'PROVISIONING':
                return {
                    backgroundColor: '#E3F2FD',
                    color: '#1976D2'
                };
            case 'REJECTED':
                return {
                    backgroundColor: '#FCE4EC',
                    color: '#C2185B'
                };
            case 'TERMINATED':
            case 'ERROR':
                return {
                    backgroundColor: '#FFEBEE',
                    color: '#f44336'
                };
            case 'EXPIRED':
                return {
                    backgroundColor: '#F3E5F5',
                    color: '#7B1FA2'
                };
            case 'INACTIVE':
                return {
                    backgroundColor: '#F5F5F5',
                    color: '#9E9E9E'
                };
            case 'UNKNOWN':
                return {
                    backgroundColor: '#f5f5f5',
                    color: '#666'
                };
            default:
                return {
                    backgroundColor: '#f5f5f5',
                    color: '#666'
                };
        }
    };

    // Grid columns for user groups
    const groupGridColumns = [
        {
            name: 'groupName',
            header: 'Group Name',
            defaultFlex: 2,
            minWidth: 150,
        },
        {
            name: 'applicationName',
            header: 'Application',
            defaultFlex: 1,
            minWidth: 100,
        },
        {
            name: 'description',
            header: 'Description',
            defaultFlex: 2,
            minWidth: 100,
        },
        {
            name: 'assignedDate',
            header: 'Assigned Date',
            defaultWidth: 130,
            render: ({value}: { value: string }) => {
                if (!value) return '';
                return value.split('T')[0];
            }
        },
        {
            name: 'requestCreatedDate',
            header: 'Request Created Date',
            defaultWidth: 180,
            render: ({value}: { value: string }) => {
                if (!value) return '';
                return value.split('T')[0];
            }
        },
        {
            name: 'startDate',
            header: 'Start Date',
            defaultFlex: 1,
            minWidth: 100,
            render: ({value}: { value: string }) => {
                if (!value) return '';
                return value.split('T')[0];
            }
        },
        {
            name: 'endDate',
            header: 'End Date',
            defaultFlex: 1,
            minWidth: 100,
            render: ({value}: { value: string }) => {
                if (!value) return '';
                return value.split('T')[0];
            }
        },
        {
            name: 'groupType',
            header: 'Type',
            defaultWidth: 100,
        },
        {
            name: 'action',
            header: 'Action',
            defaultWidth: 120,
            render: ({data}: { data: UserGroup }) => {
                const isEndDateInPast = data.endDate ? new Date(data.endDate) < new Date() : false;
                return (
                    <span title={isEndDateInPast ? 'Cannot revoke - end date has already passed' : ''}>
                        <Button
                            onClick={() => handleRevokeClick(data)}
                            variation="outline"
                            disabled={isEndDateInPast}
                        >
                            Revoke
                        </Button>
                    </span>
                );
            }
        }
    ];

    // Grid columns for user requests
    const requestGridColumns = [
        {
            name: 'id',
            header: 'Request ID',
            defaultWidth: 100,
            type: 'number',
        },
        {
            name: 'applicationName',
            header: 'Application',
            defaultFlex: 1,
            minWidth: 150,
        },
        {
            name: 'roleName',
            header: 'Role',
            defaultFlex: 1,
            minWidth: 150,
        },
        {
            name: 'status',
            header: 'Status',
            defaultWidth: 100,
            render: ({value}: { value: string }) => {
                const statusStyles = getStatusStyles(value);

                return (
                    <span style={{
                        padding: '3px 8px',
                        borderRadius: '4px',
                        backgroundColor: statusStyles.backgroundColor,
                        color: statusStyles.color,
                        fontSize: '12px',
                        fontWeight: 'bold'
                    }}>
                        {value}
                    </span>
                );
            }
        },
        {
            name: 'assignedTo',
            header: 'Assigned To',
            defaultFlex: 1,
            minWidth: 120,
        },
        {
            name: 'createdDate',
            header: 'Created Date',
            defaultWidth: 130,
            render: ({value}: { value: string }) => {
                if (!value) return '';
                return value.split('T')[0];
            }
        },
        {
            name: 'action',
            header: 'Action',
            defaultWidth: 100,
            render: ({data}: { data: UserAppRequestDto }) => {
                return (
                    <Button
                        onClick={() => handleViewRequestDetails(data)}
                        variation="outline"
                    >
                        Details
                    </Button>
                );
            }
        }
    ];

    if (!user) {
        return <div>Loading...</div>;
    }

    const statusStyles = getStatusStyles(user.userStatus);
    const showResendButton = user.userStatus?.toUpperCase() === 'INVITED';

    return (
        <React.Fragment>
            <NavBar/>

            <div style={{marginLeft: '20px', marginRight: '20px', minHeight: '300px'}}>
                <div className="subheading grid-row">
                    <div className="grid-col flex-1">
                        <h3 className="subheading-title" style={{
                            textAlign: 'center',
                            marginBottom: '20px',
                            marginTop: '40px'
                        }}>
                            User Management - {user.firstName} {user.lastName}
                        </h3>
                    </div>
                </div>

                <div style={{
                    padding: '30px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    margin: '0 auto',
                    maxWidth: '1200px',
                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
                }}>
                    {/* User Details Section */}
                    <div style={{marginBottom: '40px'}}>
                        <h4 style={{
                            margin: '0 0 20px 0',
                            color: '#00607f',
                            borderBottom: '2px solid #00607f',
                            paddingBottom: '8px'
                        }}>
                            User Details
                        </h4>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '30px',
                            marginBottom: '20px'
                        }}>
                            <div style={{backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px'}}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '15px'
                                }}>
                                    <h5 style={{margin: '0', color: '#00607f'}}>Personal Information</h5>
                                    <div style={{fontSize: '14px', display: 'flex', gap: '10px'}}>
                                        <Button
                                            onClick={handleEditUser}
                                            variation="outline"
                                        >
                                            Edit
                                        </Button>
                                        <Button
                                            onClick={handleViewHistory}
                                            variation="outline"
                                        >
                                            View History
                                        </Button>
                                    </div>
                                </div>
                                <p style={{margin: '8px 0'}}><strong>User ID:</strong> {user.userId}</p>
                                <p style={{margin: '8px 0'}}><strong>Username:</strong> {user.userLogin}</p>
                                <p style={{margin: '8px 0'}}><strong>First Name:</strong> {user.firstName}</p>
                                <p style={{margin: '8px 0'}}><strong>Last Name:</strong> {user.lastName}</p>
                                <p style={{margin: '8px 0'}}><strong>Email:</strong> {user.userEmail}</p>
                                <p style={{margin: '8px 0'}}>
                                    <strong>Phone:</strong> {displayPhoneNumber(user.userPhone) || 'Not provided'}</p>
                            </div>

                            <div style={{backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px'}}>
                                <h5 style={{margin: '0 0 15px 0', color: '#00607f'}}>Account Information</h5>
                                <p style={{margin: '8px 0'}}><strong>User Type:</strong> {user.userType}</p>
                                <p style={{margin: '8px 0'}}>
                                    <strong>Status:</strong>
                                    <span style={{
                                        padding: '4px 10px',
                                        borderRadius: '4px',
                                        backgroundColor: statusStyles.backgroundColor,
                                        color: statusStyles.color,
                                        marginLeft: '8px',
                                        fontSize: '14px',
                                        fontWeight: 'bold'
                                    }}>
                                        {user.userStatus || ''}
                                    </span>
                                </p>
                                <p style={{margin: '8px 0'}}><strong>IDP GUID:</strong> {user.userIdpGuid || ''}
                                </p>
                                <p style={{ margin: '8px 0' }}>
                                    <strong>Created Date:</strong> {user.createdDate && !Number.isNaN(new Date(user.createdDate).getTime()) ? new Date(user.createdDate).toLocaleString() : ''}
                                </p>
                                <p style={{ margin: '8px 0' }}>
                                    <strong>Modified Date:</strong> {user.modifiedDate && !Number.isNaN(new Date(user.modifiedDate).getTime()) ? new Date(user.modifiedDate).toLocaleString() : ''}
                                </p>
                            </div>
                        </div>

                        {user.notes && (
                            <div style={{
                                backgroundColor: '#f8f9fa',
                                padding: '20px',
                                borderRadius: '8px',
                                marginBottom: '20px'
                            }}>
                                <h5 style={{margin: '0 0 10px 0', color: '#00607f'}}>Notes</h5>
                                <p style={{margin: 0, whiteSpace: 'pre-wrap'}}>{base64Utils.decode(user.notes)}</p>
                            </div>
                        )}

                        {/* Address Information Section */}
                        {(user.addressLine1 || user.city || user.state || user.zip) && (
                            <div style={{
                                backgroundColor: '#f8f9fa',
                                padding: '20px',
                                borderRadius: '8px',
                                marginBottom: '20px'
                            }}>
                                <h5 style={{margin: '0 0 15px 0', color: '#00607f'}}>Address Information</h5>
                                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px'}}>
                                    <div>
                                        {user.addressLine1 && (
                                            <p style={{margin: '8px 0'}}>
                                                <strong>Address:</strong> {user.addressLine1}{user.addressLine2 && <>
                                                <br/>{user.addressLine2}</>}</p>
                                        )}
                                    </div>
                                    <div>
                                        {user.city && (
                                            <p style={{margin: '8px 0'}}><strong>City:</strong> {user.city}</p>
                                        )}
                                        {user.state && (
                                            <p style={{margin: '8px 0'}}><strong>State:</strong> {user.state}</p>
                                        )}
                                        {user.zip && (
                                            <p style={{margin: '8px 0'}}><strong>ZIP:</strong> {user.zip}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Organization Details Section */}
                        {(user.organizationName || user.organizationType || user.stateIssuedId) && (
                            <div style={{
                                backgroundColor: '#f0f8ff',
                                padding: '20px',
                                borderRadius: '8px',
                                marginBottom: '20px',
                                border: '1px solid #00607f'
                            }}>
                                <h5 style={{margin: '0 0 15px 0', color: '#00607f'}}>Organization Details</h5>
                                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px'}}>
                                    <div>
                                        {user.organizationName && (
                                            <p style={{margin: '8px 0'}}><strong>Organization
                                                Name:</strong> {user.organizationName}</p>
                                        )}
                                        {user.organizationType && (
                                            <p style={{margin: '8px 0'}}><strong>Organization
                                                Type:</strong> {user.organizationType}</p>
                                        )}
                                    </div>
                                    <div>
                                        {user.stateIssuedId && (
                                            <p style={{margin: '8px 0'}}><strong>State Issued
                                                ID:</strong> {user.stateIssuedId}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* General Error Display */}
                        {errors.general && (
                            <div style={{color: "red", marginTop: "10px", marginBottom: "10px", textAlign: "center"}}>
                                {errors.general}
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div style={{display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap'}}>
                            {showResendButton && (
                                <div>
                                    <Button
                                        onClick={handleResendVerificationEmail}
                                        disabled={resendingEmail}
                                    >
                                        {resendingEmail ? 'Sending...' : 'Resend Verification Email'}
                                    </Button>
                                    {errors.resendEmail && (
                                        <div style={{
                                            color: "red",
                                            marginTop: "5px",
                                            fontSize: "14px",
                                            textAlign: "center"
                                        }}>
                                            {errors.resendEmail}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Assigned Groups Section */}
                    <div style={{marginBottom: '40px'}}>
                        <h4 style={{
                            margin: '0 0 20px 0',
                            color: '#00607f',
                            borderBottom: '2px solid #00607f',
                            paddingBottom: '8px'
                        }}>
                            Assigned Roles ({filteredGroups.length})
                            {dynamicApplications && dynamicApplications.length > 0 && (
                                <span
                                    style={{fontSize: '14px', color: '#666', fontWeight: 'normal', marginLeft: '10px'}}>
                                    (Showing only for your applications)
                                </span>
                            )}
                        </h4>

                        {groupsLoading ? (
                            <div style={{textAlign: 'center', padding: '20px'}}>
                                Loading assigned groups...
                            </div>
                        ) : filteredGroups.length > 0 ? (
                            <ReactDataGrid
                                licenseKey="AppName=CoherenceApp,Company=Coherence,ExpiryDate=2025-04-15T00:00:00.000Z,LicenseDeveloperCount=1,LicenseType=single_app,Ref=CoherenceLicenseRef,Z=983320388-1513273079-1127323721-2081795733-199375339958733905"
                                idProperty="groupId"
                                pagination
                                columns={groupGridColumns}
                                dataSource={filteredGroups}
                                sortable={true}
                                defaultLimit={10}
                                rowHeight={50}
                                enableColumnAutosize={true}
                                style={{minHeight: 300}}
                            />
                        ) : (
                            <div style={{
                                textAlign: 'center',
                                padding: '40px',
                                color: '#666',
                                fontSize: '16px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px'
                            }}>
                                <h5 style={{marginBottom: '10px'}}>No Groups Assigned</h5>
                                <p>This user is not currently assigned to any groups for your applications.</p>
                            </div>
                        )}
                    </div>

                    {/* Role Requests Section */}
                    <div style={{marginBottom: '30px'}}>
                        <h4 style={{
                            margin: '0 0 20px 0',
                            color: '#00607f',
                            borderBottom: '2px solid #00607f',
                            paddingBottom: '8px'
                        }}>
                            Role Requests ({filteredRequests.length})
                            {dynamicApplications && dynamicApplications.length > 0 && (
                                <span
                                    style={{fontSize: '14px', color: '#666', fontWeight: 'normal', marginLeft: '10px'}}>
                                    (Showing only for your applications)
                                </span>
                            )}
                        </h4>

                        {requestsLoading ? (
                            <div style={{textAlign: 'center', padding: '20px'}}>
                                Loading role requests...
                            </div>
                        ) : filteredRequests.length > 0 ? (
                            <ReactDataGrid
                                licenseKey="AppName=CoherenceApp,Company=Coherence,ExpiryDate=2025-04-15T00:00:00.000Z,LicenseDeveloperCount=1,LicenseType=single_app,Ref=CoherenceLicenseRef,Z=983320388-1513273079-1127323721-2081795733-199375339958733905"
                                idProperty="id"
                                pagination
                                columns={requestGridColumns}
                                dataSource={filteredRequests}
                                sortable={true}
                                defaultLimit={10}
                                rowHeight={50}
                                enableColumnAutosize={true}
                                style={{minHeight: 300}}
                                defaultSortInfo={[{name: 'createdDate', dir: -1}]}
                            />
                        ) : (
                            <div style={{
                                textAlign: 'center',
                                padding: '40px',
                                color: '#666',
                                fontSize: '16px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px'
                            }}>
                                <h5 style={{marginBottom: '10px'}}>No Role Requests</h5>
                                <p>This user has no role assignment requests for your applications.</p>
                            </div>
                        )}
                    </div>

                    {/* Back Button */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        paddingTop: '20px',
                        borderTop: '1px solid #eee'
                    }}>
                        <Button onClick={handleBack}>Back to User Management</Button>
                    </div>
                </div>
            </div>

            {/* User History Dialog */}
            <Dialog
                open={historyDialogOpen}
                onClose={handleCloseHistoryDialog}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle style={{backgroundColor: '#00607f', color: 'white', padding: '16px 24px'}}>
                    User History - {user.firstName} {user.lastName}
                </DialogTitle>
                <DialogContent style={{padding: '24px'}}>
                    {historyLoading ? (
                        <div style={{textAlign: 'center', padding: '40px'}}>
                            Loading history...
                        </div>
                    ) : userHistory.length === 0 ? (
                        <div style={{textAlign: 'center', padding: '40px', color: '#666'}}>
                            <h5>No History Records Found</h5>
                            <p>This user has no history records yet.</p>
                        </div>
                    ) : (
                        <div style={{maxHeight: '600px', overflowY: 'auto'}}>
                            {userHistory.map((history, index) => (
                                <div
                                    key={history.historyId}
                                    style={{
                                        marginBottom: '20px',
                                        padding: '20px',
                                        backgroundColor: index % 2 === 0 ? '#f8f9fa' : '#ffffff',
                                        borderRadius: '8px',
                                        border: '1px solid #dee2e6'
                                    }}
                                >
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        marginBottom: '10px'
                                    }}>
                                        <div>
                                            <strong style={{color: '#00607f', fontSize: '16px'}}>
                                                {history.actionType}
                                            </strong>
                                            <span style={{marginLeft: '10px', color: '#666', fontSize: '14px'}}>
                                                {new Date(history.modifiedDate).toLocaleString()}
                                            </span>
                                        </div>
                                        <div style={{fontSize: '14px', color: '#666'}}>
                                            Modified by: <strong>{history.modifiedBy || 'System'}</strong>
                                        </div>
                                    </div>

                                    {history.changeDescription && (
                                        <div
                                            style={{
                                                padding: '12px',
                                                backgroundColor: '#fff3cd',
                                                borderLeft: '4px solid #ffc107',
                                                borderRadius: '4px',
                                                marginBottom: '10px',
                                                fontSize: '14px'
                                            }}
                                        >
                                            <strong>Changes:</strong> {history.changeDescription}
                                        </div>
                                    )}

                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: '10px',
                                        marginTop: '10px'
                                    }}>
                                        <div>
                                            <p style={{margin: '5px 0', fontSize: '14px'}}>
                                                <strong>First Name:</strong> {history.firstName || 'N/A'}
                                            </p>
                                            <p style={{margin: '5px 0', fontSize: '14px'}}>
                                                <strong>Last Name:</strong> {history.lastName || 'N/A'}
                                            </p>
                                            <p style={{margin: '5px 0', fontSize: '14px'}}>
                                                <strong>Email:</strong> {history.userEmail || 'N/A'}
                                            </p>
                                            <p style={{margin: '5px 0', fontSize: '14px'}}>
                                                <strong>Phone:</strong> {displayPhoneNumber(history.userPhone) || 'N/A'}
                                            </p>
                                        </div>
                                        <div>
                                            <p style={{margin: '5px 0', fontSize: '14px'}}>
                                                <strong>Status:</strong> {history.userStatus || 'N/A'}
                                            </p>
                                            <p style={{margin: '5px 0', fontSize: '14px'}}>
                                                <strong>City:</strong> {history.city || 'N/A'}
                                            </p>
                                            <p style={{margin: '5px 0', fontSize: '14px'}}>
                                                <strong>State:</strong> {history.state || 'N/A'}
                                            </p>
                                            <p style={{margin: '5px 0', fontSize: '14px'}}>
                                                <strong>ZIP:</strong> {history.zip || 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </DialogContent>
                <DialogActions style={{padding: '16px 24px'}}>
                    <Button onClick={handleCloseHistoryDialog} variation="outline">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Revoke Dialog */}
            <Dialog
                open={revokeDialogOpen}
                onClose={handleCloseRevokeDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle style={{backgroundColor: '#00607f', color: 'white', padding: '16px 24px'}}>
                    Revoke Group Access
                </DialogTitle>
                <DialogContent style={{padding: '24px'}}>
                    {selectedGroup && (
                        <div style={{marginBottom: '20px'}}>
                            <p style={{margin: '5px 0', fontSize: '14px'}}>
                                <strong>Group:</strong> {selectedGroup.groupName}
                            </p>
                            <p style={{margin: '5px 0', fontSize: '14px'}}>
                                <strong>Application:</strong> {selectedGroup.applicationName}
                            </p>
                        </div>
                    )}

                    <FormControl component="fieldset" style={{marginBottom: '20px', width: '100%'}}>
                        <RadioGroup
                            value={revokeOption}
                            onChange={handleRevokeOptionChange}
                        >
                            <FormControlLabel
                                value="immediate"
                                control={<Radio style={{color: '#00607f'}}/>}
                                label="Revoke Immediately"
                                style={{marginBottom: '10px'}}
                            />
                            <FormControlLabel
                                value="date"
                                control={<Radio style={{color: '#00607f'}}/>}
                                label="Schedule Revocation"
                            />
                        </RadioGroup>
                    </FormControl>

                    {revokeOption === 'date' && (
                        <div style={{marginBottom: '20px'}}>
                            <InputText
                                label="Revocation Date"
                                name="revokeDate"
                                value={revokeDate}
                                type="date"
                                onChange={handleRevokeDateChange}
                                mandatoryLabel={true}
                                readOnly={revokingGroup}
                            />
                        </div>
                    )}

                    <div style={{marginBottom: '10px'}}>
                        <label htmlFor="revoke-notes"
                               style={{display: 'block', marginBottom: '8px', fontWeight: 'bold'}}>
                            <b>Notes:</b> <span style={{color: 'red'}}>*</span>
                        </label>
                        <ReactQuill
                            id="revoke-notes"
                            value={revokeNotes}
                            onChange={handleRevokeNotesChange}
                            style={{maxHeight: '150px', overflowY: 'auto'}}
                            placeholder="Please provide a reason for revoking access (required)"
                            modules={{
                                toolbar: [
                                    ['bold', 'italic', 'underline', 'strike'],
                                    [{list: 'ordered'}, {list: 'bullet'}],
                                    [{align: []}],
                                    ['clean'],
                                ],
                            }}
                        />
                    </div>
                </DialogContent>
                <DialogActions style={{padding: '16px 24px'}}>
                    <Button
                        onClick={handleCloseRevokeDialog}
                        variation="outline"
                        disabled={revokingGroup}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleRevokeSubmit}
                        disabled={!isRevokeSubmitEnabled() || revokingGroup}
                    >
                        {revokingGroup ? 'Revoking...' : 'Submit'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Request Details Dialog */}
            <Dialog
                open={requestDetailsDialogOpen}
                onClose={handleCloseRequestDetailsDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle style={{backgroundColor: '#00607f', color: 'white', padding: '16px 24px'}}>
                    Request Details
                </DialogTitle>
                <DialogContent style={{padding: '24px'}}>
                    {selectedRequest && (
                        <>
                            {/* User Information */}
                            <div style={{marginBottom: '20px'}}>
                                <h5 style={{margin: '0 0 15px 0', color: '#00607f', borderBottom: '2px solid #00607f', paddingBottom: '8px'}}>
                                    User Information
                                </h5>
                                <div style={{backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '6px'}}>
                                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px'}}>
                                        <p style={{margin: '5px 0'}}><strong>Username:</strong> {selectedRequest.userLogin}</p>
                                        <p style={{margin: '5px 0'}}><strong>Name:</strong> {selectedRequest.firstName} {selectedRequest.lastName}</p>
                                        <p style={{margin: '5px 0'}}><strong>Email:</strong> {selectedRequest.userEmail}</p>
                                        <p style={{margin: '5px 0'}}><strong>User ID:</strong> {selectedRequest.userId}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Request Information */}
                            <div style={{marginBottom: '20px'}}>
                                <h5 style={{margin: '0 0 15px 0', color: '#00607f', borderBottom: '2px solid #00607f', paddingBottom: '8px'}}>
                                    Request Information
                                </h5>
                                <div style={{backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '6px'}}>
                                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px'}}>
                                        <p style={{margin: '5px 0'}}><strong>Request ID:</strong> {selectedRequest.id}</p>
                                        <p style={{margin: '5px 0'}}>
                                            <strong>Status:</strong>
                                            <span style={{
                                                padding: '3px 8px',
                                                borderRadius: '4px',
                                                backgroundColor: getStatusStyles(selectedRequest.status).backgroundColor,
                                                color: getStatusStyles(selectedRequest.status).color,
                                                marginLeft: '8px',
                                                fontSize: '12px',
                                                fontWeight: 'bold'
                                            }}>
                                                {selectedRequest.status}
                                            </span>
                                        </p>
                                        <p style={{margin: '5px 0'}}><strong>Application:</strong> {selectedRequest.applicationName} ({selectedRequest.applicationCode})</p>
                                        <p style={{margin: '5px 0'}}><strong>Role:</strong> {selectedRequest.roleName} ({selectedRequest.roleCode})</p>
                                        <p style={{margin: '5px 0'}}><strong>Assigned To:</strong> {selectedRequest.assignedTo}</p>
                                        <p style={{margin: '5px 0'}}><strong>Operational Flag:</strong> {selectedRequest.operationalFlag}</p>
                                        <p style={{margin: '5px 0'}}><strong>Created:</strong> {new Date(selectedRequest.createdDate).toLocaleString()}</p>
                                        <p style={{margin: '5px 0'}}><strong>Created By:</strong> {selectedRequest.createdBy || 'N/A'}</p>
                                        <p style={{margin: '5px 0'}}><strong>Modified:</strong> {new Date(selectedRequest.modifiedDate).toLocaleString()}</p>
                                        <p style={{margin: '5px 0'}}><strong>Modified By:</strong> {selectedRequest.modifiedBy || 'N/A'}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Role & Application Details */}
                            <div style={{marginBottom: '20px'}}>
                                <h5 style={{margin: '0 0 15px 0', color: '#00607f', borderBottom: '2px solid #00607f', paddingBottom: '8px'}}>
                                    Role & Application Details
                                </h5>
                                <div style={{backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '6px'}}>
                                    <p style={{margin: '5px 0'}}><strong>Application:</strong> {selectedRequest.applicationName} ({selectedRequest.applicationCode})</p>
                                    <p style={{margin: '5px 0'}}><strong>Role:</strong> {selectedRequest.roleName} ({selectedRequest.roleCode})</p>
                                </div>
                            </div>

                            {/* Notes */}
                            {selectedRequest.notes && (
                                <div style={{marginBottom: '20px'}}>
                                    <h5 style={{margin: '0 0 15px 0', color: '#00607f', borderBottom: '2px solid #00607f', paddingBottom: '8px'}}>
                                        Notes
                                    </h5>
                                    <div style={{
                                        padding: '15px',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '6px',
                                        border: '1px solid #dee2e6'
                                    }}>
                                        {base64Utils.decode(selectedRequest.notes)}
                                    </div>
                                </div>
                            )}

                            {/* Request Lifecycle */}
                            <div>
                                <h5 style={{margin: '0 0 15px 0', color: '#00607f', borderBottom: '2px solid #00607f', paddingBottom: '8px'}}>
                                    Request History & Lifecycle
                                </h5>
                                {requestLifecycleLoading ? (
                                    <div style={{textAlign: 'center', padding: '20px', color: '#666'}}>
                                        Loading request history...
                                    </div>
                                ) : requestLifecycle.length > 0 ? (
                                    <div style={{
                                        backgroundColor: '#f8f9fa',
                                        padding: '20px',
                                        borderRadius: '6px',
                                        border: '1px solid #dee2e6',
                                        maxHeight: '300px',
                                        overflowY: 'auto'
                                    }}>
                                        {requestLifecycle.map((lifecycleItem, index) => {
                                            const isLast = index === requestLifecycle.length - 1;
                                            const itemStatusStyles = getStatusStyles(lifecycleItem.status);

                                            return (
                                                <div key={lifecycleItem.id} style={{
                                                    position: 'relative',
                                                    paddingLeft: '40px',
                                                    paddingBottom: isLast ? '0' : '25px'
                                                }}>
                                                    {/* Timeline line */}
                                                    {!isLast && (
                                                        <div style={{
                                                            position: 'absolute',
                                                            left: '15px',
                                                            top: '25px',
                                                            bottom: '0',
                                                            width: '2px',
                                                            backgroundColor: '#dee2e6'
                                                        }}/>
                                                    )}

                                                    {/* Timeline dot */}
                                                    <div style={{
                                                        position: 'absolute',
                                                        left: '8px',
                                                        top: '8px',
                                                        width: '16px',
                                                        height: '16px',
                                                        borderRadius: '50%',
                                                        backgroundColor: itemStatusStyles.color,
                                                        border: '3px solid white',
                                                        boxShadow: '0 0 0 2px ' + itemStatusStyles.color
                                                    }}/>

                                                    {/* Timeline content */}
                                                    <div style={{
                                                        backgroundColor: 'white',
                                                        padding: '15px',
                                                        borderRadius: '6px',
                                                        border: '1px solid #dee2e6',
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                    }}>
                                                        <div style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'flex-start',
                                                            marginBottom: '10px'
                                                        }}>
                                                            <div>
                                                                <span style={{
                                                                    padding: '3px 8px',
                                                                    borderRadius: '4px',
                                                                    backgroundColor: itemStatusStyles.backgroundColor,
                                                                    color: itemStatusStyles.color,
                                                                    fontSize: '12px',
                                                                    fontWeight: 'bold',
                                                                    marginRight: '10px'
                                                                }}>
                                                                    {lifecycleItem.status}
                                                                </span>
                                                                <span style={{
                                                                    fontSize: '12px',
                                                                    color: '#666',
                                                                    fontWeight: 'bold'
                                                                }}>
                                                                    Request ID: {lifecycleItem.id}
                                                                </span>
                                                            </div>
                                                            <span style={{
                                                                fontSize: '12px',
                                                                color: '#666',
                                                                backgroundColor: lifecycleItem.operationalFlag === 'ACTIVE' ? '#e8f5e8' : '#f8f9fa',
                                                                padding: '2px 6px',
                                                                borderRadius: '3px',
                                                                border: '1px solid ' + (lifecycleItem.operationalFlag === 'ACTIVE' ? '#4CAF50' : '#dee2e6')
                                                            }}>
                                                                {lifecycleItem.operationalFlag}
                                                            </span>
                                                        </div>

                                                        <div style={{
                                                            display: 'grid',
                                                            gridTemplateColumns: '1fr 1fr',
                                                            gap: '15px',
                                                            fontSize: '14px'
                                                        }}>
                                                            <div>
                                                                <p style={{margin: '4px 0'}}>
                                                                    <strong>Created:</strong> {new Date(lifecycleItem.createdDate).toLocaleString()}
                                                                </p>
                                                                <p style={{margin: '4px 0'}}>
                                                                    <strong>Created By:</strong> {lifecycleItem.createdBy || 'N/A'}
                                                                </p>
                                                                <p style={{margin: '4px 0'}}>
                                                                    <strong>Modified:</strong> {new Date(lifecycleItem.modifiedDate).toLocaleString()}
                                                                </p>
                                                                <p style={{margin: '4px 0'}}>
                                                                    <strong>Modified By:</strong> {lifecycleItem.modifiedBy || 'N/A'}
                                                                </p>
                                                            </div>
                                                            <div>
                                                                <p style={{margin: '4px 0'}}>
                                                                    <strong>Assigned To:</strong> {lifecycleItem.assignedTo}
                                                                </p>
                                                            </div>
                                                        </div>

                                                        {lifecycleItem.notes && (
                                                            <div style={{
                                                                marginTop: '10px',
                                                                padding: '8px',
                                                                backgroundColor: '#f8f9fa',
                                                                borderRadius: '4px',
                                                                fontSize: '13px'
                                                            }}>
                                                                <strong>Notes:</strong>
                                                                <div style={{marginTop: '4px'}}>
                                                                    {base64Utils.decode(lifecycleItem.notes)}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div style={{
                                        textAlign: 'center',
                                        padding: '20px',
                                        color: '#666',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '6px'
                                    }}>
                                        No request history available
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </DialogContent>
                <DialogActions style={{padding: '16px 24px'}}>
                    <Button onClick={handleCloseRequestDetailsDialog} variation="outline">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <FooterData/>
        </React.Fragment>
    );
};

export default UserManagement;